import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ViewMode } from '@/components/FolderBrowserLayout/types';

export interface UseFolderBrowserParamsOptions {
    folderParamKey?: string;
    viewParamKey?: string;
}

export interface UseFolderBrowserParamsResult {
    currentPath: string;
    viewMode: ViewMode;
    setCurrentPath: (path: string) => void;
    setViewMode: (mode: ViewMode) => void;
}

const useFolderBrowserParams = (
    options: UseFolderBrowserParamsOptions = {}
): UseFolderBrowserParamsResult => {
    const { folderParamKey = 'folder', viewParamKey = 'view' } = options;
    const [searchParams, setSearchParams] = useSearchParams();

    const currentPath = searchParams.get(folderParamKey) || '';
    const viewMode = (searchParams.get(viewParamKey) as ViewMode) || 'cards';

    const setCurrentPath = useCallback(
        (path: string) => {
            const newParams = new URLSearchParams(searchParams);
            if (path) {
                newParams.set(folderParamKey, path);
            } else {
                newParams.delete(folderParamKey);
            }
            setSearchParams(newParams);
        },
        [searchParams, setSearchParams, folderParamKey]
    );

    const setViewMode = useCallback(
        (mode: ViewMode) => {
            const newParams = new URLSearchParams(searchParams);
            newParams.set(viewParamKey, mode);
            setSearchParams(newParams);
        },
        [searchParams, setSearchParams, viewParamKey]
    );

    return { currentPath, viewMode, setCurrentPath, setViewMode };
};

export default useFolderBrowserParams;
